import cx from 'classnames'
import type { ChangeEvent } from 'react'
import { useFormContext, useController } from 'react-hook-form'
import type { UseControllerProps } from 'react-hook-form'

import classes from './styles.module.scss'

export type Props = {
  items: { value: string; name: string }[]
  className?: string
  size?: 'small' | 'medium'
  errorMessageClass?: string
  label?: string
  placeholder?: string
  name: string
  ignoreErrorMessage?: boolean
  disabled?: boolean
  forceTouched?: boolean
  showIconValidation?: boolean
  isLoading?: boolean
  rules?: UseControllerProps['rules']
  onChange?: (e: ChangeEvent<HTMLSelectElement>) => void
  isNativePlaceholder?: boolean
  dataTest?: string
}

export default function FormSelectField(props: Props) {
  const {
    placeholder,
    label,
    name,
    ignoreErrorMessage,
    disabled,
    className,
    size,
    errorMessageClass,
    items,
    forceTouched,
    showIconValidation,
    isLoading,
    rules,
    onChange,
    isNativePlaceholder = false,
    dataTest,
  } = props

  const { control } = useFormContext()
  const {
    field,
    fieldState: { error, isTouched },
  } = useController({
    name,
    control,
    rules: rules || {},
  })
  const fieldTouched = (forceTouched ?? true) && isTouched
  const isError = !ignoreErrorMessage && error && fieldTouched

  return (
    <div className={cx(classes.root, className, { [classes.small]: size === 'small' })}>
      {label && (
        <label htmlFor={name} className={classes.label}>
          {label}
        </label>
      )}
      <select
        name={name}
        onChange={(e) => {
          field.onChange(e)
          onChange?.(e)
        }}
        id={name}
        value={field.value}
        ref={field.ref}
        className={cx(classes.select, {
          [classes.selectEmpty]: !field.value,
          [classes.selectError]: error && isTouched,
          [classes.withEndAdornment]: showIconValidation && isTouched,
        })}
        disabled={disabled}
        onBlur={field.onBlur}
        data-test={dataTest}
      >
        {placeholder && !isNativePlaceholder && (
          <option disabled hidden className={classes.option}>
            {placeholder}
          </option>
        )}

        {/*
          TODO: B2SPIN-5153 Temp solution for show native placeholder
          isNativePlaceholder Need to avoid adding additional item to items array with value=''
        */}
        {placeholder && isNativePlaceholder && (
          <option disabled selected value='' className={classes.option}>
            {placeholder}
          </option>
        )}

        {items.map(({ value: val, name: nameItem }) => (
          <option value={val} key={val} className={classes.option}>
            {nameItem}
          </option>
        ))}
      </select>
      {isError && <div className={cx(classes.error, errorMessageClass)}>{error.message}</div>}
      {isLoading && <span className={classes.loading}></span>}
    </div>
  )
}
